import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ActivacionHttpService } from './activacion-http.service';
import { LoggerService } from './logger.service';

/**
 * Servicio singleton que rastrea si hay una activación prepago en curso.
 *
 * El componente ActivacionesPrepagoComponent llama a:
 *   - register(activationId)  → cuando la activación se inicia exitosamente
 *   - unregister()            → cuando el abandono se confirma o el flujo finaliza
 *
 * Los servicios de timeout (InactivityService, TokenExpirationService) llaman a:
 *   - abandonIfActive()       → antes de ejecutar el logout por timeout, para no
 *                               dejar activaciones huérfanas en el backend.
 *
 * Este servicio es intencionalmente simple: no emite eventos ni expone Observables
 * para los consumidores de logout — solo proporciona una llamada fire-and-forget
 * que devuelve una Promise para que el flujo de timeout pueda esperarla.
 */
@Injectable({
    providedIn: 'root'
})
export class ActiveActivationService {

    private activationId: string | null = null;

    private activacionHttp = inject(ActivacionHttpService);
    private logger         = inject(LoggerService);

    /**
     * Registra la activación en curso.
     * Debe llamarse justo después de que startActivation() devuelve un activationId.
     */
    register(activationId: string): void {
        this.activationId = activationId;
        this.logger.log(`[ActiveActivationService] Activación registrada: ${activationId}`);
    }

    /**
     * Desregistra la activación (abandono confirmado o flujo completado).
     */
    unregister(): void {
        if (this.activationId) {
            this.logger.log(`[ActiveActivationService] Activación desregistrada: ${this.activationId}`);
        }
        this.activationId = null;
    }

    /** Indica si hay una activación registrada actualmente. */
    hasActiveActivation(): boolean {
        return this.activationId !== null;
    }

    /**
     * Si hay una activación en curso, llama al endpoint de abandono y espera la respuesta.
     * Siempre resuelve (nunca rechaza) para no bloquear el flujo de logout por errores de red.
     *
     * @returns Promise<void> que se resuelve cuando el abandono finaliza (o falla silenciosamente).
     */
    abandonIfActive(): Promise<void> {
        const id = this.activationId;

        if (!id) {
            return Promise.resolve();
        }

        this.logger.log(`[ActiveActivationService] Ejecutando abandono por timeout: ${id}`);

        const abandon$: Observable<void> = this.activacionHttp.abandonActivation(id).pipe(
            map(() => {
                this.logger.log(`[ActiveActivationService] Abandono exitoso: ${id}`);
                this.activationId = null;
            }),
            catchError((err) => {
                // Loguear pero no propagar — el logout debe continuar incluso si falla el abandono
                this.logger.log(`[ActiveActivationService] Error en abandono por timeout: ${JSON.stringify(err)}`);
                this.activationId = null;
                return of(undefined);
            })
        );

        return new Promise<void>(resolve => {
            abandon$.subscribe({ complete: resolve, error: () => resolve() });
        });
    }
}
